import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import clsx from 'clsx';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import { Icon } from 'shared/components/icon';
import { falClock } from 'shared/@generated/icons/falClock';
import { fasPhone } from 'shared/@generated/icons/fasPhone';
import { falLocationDot } from 'shared/@generated/icons/falLocationDot';
import {
  EvidensiaClinic,
  getEvidensiaOpeningStatus,
  getEvidensiaOpeningTimes,
} from 'shared/library/evidensia-clinics';
import type { EvidensiaClinicAction } from 'shared/components/evidensia-widget-analytics';
import { getNextEvidensiaOpening } from 'shared/library/evidensia-opening-hours';
import { useEvidensiaOpeningSchedule } from 'shared/library/use-evidensia-opening-schedule.web';

function useClinicTime(providedNow?: Date) {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => {
    if (providedNow) return;
    const update = () => setNow(new Date());
    update();
    const timer = setInterval(update, 60_000);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', update);
    };
  }, [providedNow]);
  return providedNow ?? now;
}

function useNextOpeningText(clinic: EvidensiaClinic, now: Date | null, enabled: boolean) {
  const { t, i18n } = useTranslation('components');
  const opensToday =
    now && clinic.openingHours?.opensAt && new Date(clinic.openingHours.opensAt) > now;
  const schedule = useEvidensiaOpeningSchedule(clinic.url, enabled && !opensToday);
  const next = now && enabled ? getNextEvidensiaOpening(clinic, schedule, now) : null;
  if (!next) return null;
  if (next.daysAway === 0) return t('evidensia_booking_widget.opens_today', { time: next.time });
  if (next.daysAway === 1) return t('evidensia_booking_widget.opens_tomorrow', { time: next.time });
  const day = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'sv', {
    weekday: 'long',
    timeZone: 'Europe/Stockholm',
  }).format(new Date(`${next.date}T12:00:00Z`));
  return t('evidensia_booking_widget.opens_on', { day, time: next.time });
}

export function EvidensiaClinicOpeningStatus({
  clinic,
  light = false,
  now: providedNow,
  showHours = false,
}: {
  clinic: EvidensiaClinic;
  light?: boolean;
  now?: Date;
  showHours?: boolean;
}) {
  const { t } = useTranslation('components');
  const now = useClinicTime(providedNow);
  const status = now ? getEvidensiaOpeningStatus(clinic, now) : null;
  const nextOpening = useNextOpeningText(clinic, now, showHours && status === 'closed');
  if (!status) return null;
  const times = showHours ? getEvidensiaOpeningTimes(clinic) : null;
  const today =
    showHours &&
    clinic.openingHours?.date === formatInTimeZone(now!, 'Europe/Stockholm', 'yyyy-MM-dd');
  const hoursText =
    nextOpening ??
    (showHours && today
      ? clinic.openingHours?.allDay
        ? t('evidensia_booking_widget.open_all_day')
        : times
          ? t('evidensia_booking_widget.hours_today', {
              hours: `${times.opens}–${times.closes}`,
            })
          : null
      : null);
  const closedToday = showHours && today && clinic.openingHours?.closed;

  return (
    <View
      className={'flex-row items-center gap-1.5'}
      accessibilityLiveRegion={'polite'}
      testID={'evidensia-opening-status'}
    >
      <View
        className={clsx(
          'size-1.5 rounded-full',
          status === 'open'
            ? light
              ? 'bg-green-200'
              : 'bg-brand-green'
            : light
              ? 'bg-white/60'
              : 'bg-stone-400',
        )}
      />
      <Text
        className={clsx(
          'text-xs font-semibold',
          light
            ? 'text-white'
            : status === 'open'
              ? 'text-brand-green'
              : 'text-brand-brown-dark/70',
        )}
      >
        {t(
          `evidensia_booking_widget.${status === 'open' ? 'open_now' : nextOpening ? 'closed' : closedToday ? 'closed_today' : 'closed_now'}`,
        )}
      </Text>
      {hoursText && (
        <Text
          className={clsx(
            'whitespace-nowrap text-xs',
            light ? 'text-white/90' : 'text-brand-brown-dark/70',
          )}
          testID={'evidensia-opening-hours'}
        >
          {!nextOpening && '· '}
          {hoursText}
        </Text>
      )}
    </View>
  );
}

export function EvidensiaClinicAddress({
  clinic,
  light = false,
}: {
  clinic: EvidensiaClinic;
  light?: boolean;
}) {
  if (!clinic.address) return null;
  const colorClassName = light ? 'text-white/90' : 'text-brand-brown-dark/70';
  return (
    <View className={'flex-row items-start gap-2'} testID={'evidensia-address'}>
      <Icon icon={falLocationDot} className={clsx('mt-0.5 size-3 shrink-0', colorClassName)} />
      <Text className={clsx('min-w-0 flex-1 text-xs', colorClassName)}>{clinic.address}</Text>
    </View>
  );
}

export function EvidensiaClinicContact({
  clinic,
  light = false,
  showPhoneInfo = false,
  now: providedNow,
  onAction,
}: {
  clinic: EvidensiaClinic;
  light?: boolean;
  showPhoneInfo?: boolean;
  now?: Date;
  onAction?: (action: EvidensiaClinicAction, clinic: EvidensiaClinic) => void;
}) {
  const { t } = useTranslation('components');
  const now = useClinicTime(providedNow);

  const copy = (key: string, values?: Record<string, string>) =>
    t(`evidensia_booking_widget.${key}`, values);
  const status = now ? getEvidensiaOpeningStatus(clinic, now) : null;
  const nextOpening = useNextOpeningText(clinic, now, status === 'closed');
  const hours = clinic.openingHours;
  const times = getEvidensiaOpeningTimes(clinic);
  let hoursText = copy('opening_hours');
  if (status && hours) {
    if (hours.allDay) hoursText = copy('open_all_day');
    else if (hours.closed) hoursText = copy('closed_today');
    else if (times)
      hoursText =
        hours.date === formatInTimeZone(now!, 'Europe/Stockholm', 'yyyy-MM-dd')
          ? copy('hours_today', { hours: `${times.opens}–${times.closes}` })
          : copy('open_until', { time: times.closes });
  }
  if (nextOpening) hoursText = nextOpening;
  const textClassName = clsx(
    'whitespace-nowrap text-xs',
    light ? 'text-white/90' : 'text-brand-brown-dark/70',
  );
  const iconClassName = clsx(
    'size-3 shrink-0',
    light ? 'text-white/90' : 'text-brand-brown-dark/70',
  );

  return (
    <View className={'gap-1'} testID={'evidensia-clinic-contact'}>
      <EvidensiaClinicAddress clinic={clinic} light={light} />
      {status && hours && (
        <View className={'flex-row items-center gap-2'} testID={'evidensia-opening-hours'}>
          <Icon icon={falClock} className={iconClassName} />
          <Text className={textClassName}>{hoursText}</Text>
        </View>
      )}
      {clinic.phone && (
        <View className={'flex-row items-center gap-2'} testID={'evidensia-phone'}>
          <Icon icon={fasPhone} className={iconClassName} />
          <Link
            href={`tel:${clinic.phone.replace(/[^\d+]/g, '')}`}
            textClassName={clsx(textClassName, 'underline decoration-current/30')}
            onPress={() => onAction?.('phone', clinic)}
          >
            {clinic.phone}
          </Link>
        </View>
      )}
      {showPhoneInfo && clinic.phone && clinic.phoneInfo && (
        <Text className={clsx('text-xs', light ? 'text-white/80' : 'text-brand-brown-dark/70')}>
          {clinic.phoneInfo}
        </Text>
      )}
    </View>
  );
}
